import type { LeverOverlapDeclaration, LeverOverlapRelation } from './lever-overlap-registry';

/**
 * Behavior-preserving extraction (P1 follow-up, head 22504caadb; function-size
 * follow-up, head 4214f74226): this is the ORIGINAL labor/revenue/transport/
 * SG&A cross-cluster declaration logic that used to live inline in
 * `lever-overlap-registry.ts`'s `buildDeclarations()`, moved out verbatim
 * (same declarations, same order, same results), then split into per-group
 * functions of <=60 lines composed by a <=60-line parent. Pure functions -
 * the family arrays they need are passed in by the caller, so this file
 * carries no runtime import of `lever-overlap-registry.ts` - only the
 * type-only import above (erased at compile time) - avoiding a circular
 * require.
 */

function decl(
  familyA: string,
  familyB: string,
  relation: LeverOverlapRelation,
  basis: string
): LeverOverlapDeclaration {
  return Object.freeze({
    familyA,
    familyB,
    relation,
    evidenceRequirement: relation === 'overlapping' ? 'deconfliction_receipt' : 'structural_policy',
    basis,
  });
}

// Every distinct (i, j) pair within one array, i < j - the shape shared by
// every "these families all overlap/are disjoint with each other" cluster.
function selfPairs(
  items: readonly string[],
  relation: LeverOverlapRelation,
  basis: string
): LeverOverlapDeclaration[] {
  const out: LeverOverlapDeclaration[] = [];
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      out.push(decl(items[i], items[j], relation, basis));
    }
  }
  return out;
}

// Every (a, b) pair across two arrays - the shape shared by every simple
// "cluster A vs cluster B" cross-cluster declaration.
function crossPairs(
  as: readonly string[],
  bs: readonly string[],
  relation: LeverOverlapRelation,
  basis: string
): LeverOverlapDeclaration[] {
  const out: LeverOverlapDeclaration[] = [];
  for (const a of as) {
    for (const b of bs) {
      out.push(decl(a, b, relation, basis));
    }
  }
  return out;
}

// Cost-side purchased transportation is structurally disjoint from revenue-side pricing (the
// board rule: transport is cost-side only; pricing is revenue/AGP-side) and from internal labor
// cost (external carrier cost, not internal labor cost). Kept as one function (not two crossPairs
// calls) so the per-transport interleaving of revenue-then-labor pairs matches the original
// emission order exactly.
function transportVsRevenueAndLaborDisjoint(
  purchasedTransportFamilies: readonly string[],
  revenueSideFamilies: readonly string[],
  laborCostFamilies: readonly string[]
): LeverOverlapDeclaration[] {
  const out: LeverOverlapDeclaration[] = [];
  for (const transport of purchasedTransportFamilies) {
    for (const pricing of revenueSideFamilies) {
      out.push(
        decl(
          transport,
          pricing,
          'disjoint',
          'Purchased transportation cost-side recovery does not share dollars with revenue-side price realization.'
        )
      );
    }
    for (const labor of laborCostFamilies) {
      out.push(
        decl(
          transport,
          labor,
          'disjoint',
          'Purchased transportation spend is external carrier cost, not internal labor cost.'
        )
      );
    }
  }
  return out;
}

// Non-labor SG&A spend is structurally disjoint from labor cost, purchased transportation, and
// revenue realization: different vendors, different P&L mechanics. Kept as one function (not
// three crossPairs calls) so the per-spend interleaving matches the original emission order.
function sgaSpendVsClustersDisjoint(
  sgaSpendFamilies: readonly string[],
  laborCostFamilies: readonly string[],
  revenueSideFamilies: readonly string[],
  purchasedTransportFamilies: readonly string[]
): LeverOverlapDeclaration[] {
  const out: LeverOverlapDeclaration[] = [];
  for (const spend of sgaSpendFamilies) {
    for (const labor of laborCostFamilies) {
      out.push(
        decl(
          spend,
          labor,
          'disjoint',
          'Non-labor SG&A vendor spend does not share dollars with internal labor cost programs.'
        )
      );
    }
    for (const pricing of revenueSideFamilies) {
      out.push(
        decl(
          spend,
          pricing,
          'disjoint',
          'Non-labor SG&A vendor spend does not share dollars with revenue realization.'
        )
      );
    }
    for (const transport of purchasedTransportFamilies) {
      out.push(decl(spend, transport, 'disjoint', 'Corporate SG&A spend is not purchased transportation.'));
    }
  }
  return out;
}

export type CoreClusterFamilies = Readonly<{
  laborCostFamilies: readonly string[];
  revenueSideFamilies: readonly string[];
  purchasedTransportFamilies: readonly string[];
  sgaSpendFamilies: readonly string[];
}>;

// Parent: composes the groups above in the ORIGINAL block order. Each group
// covers exactly one comment-delimited block from the pre-split function, so
// this list is a faithful table of contents for the same 7 blocks.
export function buildCoreClusterDeclarations(
  families: CoreClusterFamilies
): readonly LeverOverlapDeclaration[] {
  const { laborCostFamilies, revenueSideFamilies, purchasedTransportFamilies, sgaSpendFamilies } =
    families;

  return [
    // Labor-cost cluster: role removal, span change, workforce programs, and automation
    // cost-release all act on the same internal labor cost pools — every pair overlaps.
    ...selfPairs(
      laborCostFamilies,
      'overlapping',
      'Both families reduce the same internal labor cost pools; shared-scope receipts required.'
    ),
    // Revenue-side pricing families overlap each other (same customer/lane revenue lines).
    ...selfPairs(
      revenueSideFamilies,
      'overlapping',
      'Both families act on the same customer and lane revenue realization.'
    ),
    // Purchased-transportation levers all attack the same buy-side spend, so every
    // pair overlaps and a de-confliction receipt is required before compositing.
    ...selfPairs(
      purchasedTransportFamilies,
      'overlapping',
      'Both families reduce the same purchased-transportation buy-side spend; shared-scope receipts required.'
    ),
    ...transportVsRevenueAndLaborDisjoint(purchasedTransportFamilies, revenueSideFamilies, laborCostFamilies),
    // Internal labor families and revenue-side pricing act on different P&L lines.
    ...crossPairs(
      laborCostFamilies,
      revenueSideFamilies,
      'disjoint',
      'Internal labor cost reduction does not share dollars with revenue realization.'
    ),
    ...sgaSpendVsClustersDisjoint(sgaSpendFamilies, laborCostFamilies, revenueSideFamilies, purchasedTransportFamilies),
    // SG&A spend families are distinct vendor categories (travel-and-expense
    // vendors, non-transport indirect vendors) with no shared dollars; declare
    // every pair disjoint explicitly rather than leaving it UNKNOWN.
    ...selfPairs(
      sgaSpendFamilies,
      'disjoint',
      'Distinct corporate SG&A vendor categories do not share dollars.'
    ),
  ];
}
